/**
 * 创建章节对话框组件
 *
 * 提供用户友好的界面来创建新章节
 * 包含完整的表单验证、错误处理和用户反馈
 *
 * 安全特性：
 * - 组件卸载后状态更新防护
 * - 输入验证和错误提示
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { Chapter, ChapterCreate } from '../../types/chapter';
import { isValidChapterNum, isValidChapterName } from '../../types/chapter';
import { ChapterService, ValidationError } from '../../services/chapter';

// 初始表单数据
const INITIAL_FORM_DATA: Omit<ChapterCreate, 'workspace_id' | 'content'> = {
  num: 1,
  name: ''
};

// SVG图标常量
const CLOSE_ICON = (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const LOADING_SPINNER = (
  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const ERROR_ICON = (
  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// 表单验证函数
function validateFormData(data: Omit<ChapterCreate, 'workspace_id'>): Record<string, string> {
  const errors: Record<string, string> = {};

  // 章节序号验证
  if (!isValidChapterNum(data.num)) {
    errors.num = '章节序号必须是1-100000之间的正整数';
  }

  // 章节名称验证
  if (!isValidChapterName(data.name)) {
    errors.name = '章节名称长度必须在1-200字符之间';
  }

  return errors;
}

// 表单字段组件
interface FormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function FormField({ label, error, required, children }: FormFieldProps) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-900 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// 组件Props接口
interface CreateChapterDialogProps {
  /** 工作区ID */
  workspaceId: number;
  /** 对话框显示状态 */
  isOpen: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 创建成功回调 */
  onSuccess?: (chapter: Chapter) => void;
  /** 可选: ChapterService实例 */
  chapterService?: ChapterService;
  /** 当前工作区的所有章节，用于自动计算下一个章节序号 */
  existingChapters?: Chapter[];
}

export const CreateChapterDialog: React.FC<CreateChapterDialogProps> = ({
  workspaceId,
  isOpen,
  onClose,
  onSuccess,
  chapterService: externalChapterService,
  existingChapters = []
}: CreateChapterDialogProps) => {
  // 使用ref跟踪组件是否已挂载，防止组件卸载后的状态更新
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 表单状态
  const [formData, setFormData] = useState<Omit<ChapterCreate, 'workspace_id' | 'content'>>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // 服务实例使用全局共享的wsService单例
  const chapterServiceRef = useRef<ChapterService | null>(null);

  // 初始化ChapterService，使用全局共享的wsService单例
  const getChapterService = (): ChapterService => {
    if (!chapterServiceRef.current) {
      chapterServiceRef.current = externalChapterService || new ChapterService(undefined);
    }
    return chapterServiceRef.current;
  };

  // 清理逻辑（如果传入了externalChapterService，不需要清理）
  useEffect(() => {
    return () => {
      // 如果没有传入externalChapterService，不需要清理wsService（因为它是全局共享的）
    };
  }, []);

  // 当对话框打开时，重置表单并自动计算下一个章节序号
  useEffect(() => {
    if (isOpen) {
      // 计算下一个章节序号（最大章节序号+1）
      const maxNum = existingChapters.length > 0
        ? Math.max(...existingChapters.map(c => c.num))
        : 0;
      const nextNum = maxNum + 1;

      resetForm();
      setFormData(prev => ({ ...prev, num: nextNum }));
    }
  }, [isOpen, existingChapters]);

  // 处理章节序号变化
  const handleNumChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = value === '' ? 0 : parseInt(value, 10);

    // 限制输入范围
    if (isNaN(numValue) || numValue < 0) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      num: numValue
    }));

    // 清除该字段的错误
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.num;
      return newErrors;
    });
  }, []);

  // 处理章节名称变化
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 200);

    setFormData(prev => ({
      ...prev,
      name: value
    }));

    // 清除该字段的错误
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.name;
      return newErrors;
    });
  }, []);

  // 处理表单提交
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // 清除表单级错误
    setFormError(null);

    // 验证表单
    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 前端验证：检查章节序号是否已存在
    const duplicateChapter = existingChapters.find(c => c.num === formData.num);
    if (duplicateChapter) {
      setErrors({ num: `章节序号 ${formData.num} 已存在（"${duplicateChapter.name}"），请使用其他序号` });
      return;
    }

    // 构建完整的表单数据，content 设为占位内容
    const createData: ChapterCreate = {
      ...formData,
      workspace_id: workspaceId,
      content: `# ${formData.name}\n\n暂无详细内容，请在编辑器中添加。`
    };

    setIsSubmitting(true);

    try {
      const newChapter = await getChapterService().createChapter(workspaceId, createData);

      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;

      // 创建成功
      onSuccess?.(newChapter);
      onClose();
      resetForm();
    } catch (error) {
      // 记录详细错误到日志（不上报到用户）
      console.error('[CreateChapterDialog] 创建章节失败:', error);

      // 检查组件是否仍然挂载
      if (!isMountedRef.current) return;

      if (error instanceof ValidationError) {
        if (error.message.includes('序号') || error.message.includes('num')) {
          setErrors({ num: '章节序号已存在，请使用其他序号' });
        } else if (error.message.includes('名称') || error.message.includes('name')) {
          setErrors({ name: error.message });
        } else {
          setFormError(error.message);
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : '创建失败，请检查网络连接后重试';
        if (errorMessage.includes('重复') || errorMessage.includes('duplicate') || errorMessage.includes('已存在')) {
          setErrors({ num: '章节序号已存在，请使用其他序号' });
        } else {
          setFormError(errorMessage);
        }
      }
    } finally {
      // 检查组件是否仍然挂载
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [formData, workspaceId, getChapterService, onSuccess, onClose, existingChapters]);

  // 重置表单
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    setFormError(null);
  }, []);

  // 处理遮罩点击
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // 不显示时返回null
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* 对话框标题 */}
        <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">创建新章节</h2>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="关闭"
            >
              {CLOSE_ICON}
            </button>
          </div>
        </div>

        {/* 表单内容 */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* 表单级错误 */}
          {formError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                {ERROR_ICON}
                <p className="ml-2 text-red-700">{formError}</p>
              </div>
            </div>
          )}

          {/* 章节序号 */}
          <FormField label="章节序号" error={errors.num} required>
            <input
              type="number"
              value={formData.num || ''}
              onChange={handleNumChange}
              placeholder="1"
              min={1}
              max={100000}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                ${errors.num ? 'border-red-300' : 'border-gray-200'}
                disabled:bg-gray-100 disabled:cursor-not-allowed`}
            />
            <div className="mt-1 text-xs text-gray-700">
              请输入1-100000之间的正整数
            </div>
          </FormField>

          {/* 章节名称 */}
          <FormField label="章节名称" error={errors.name} required>
            <input
              type="text"
              value={formData.name}
              onChange={handleNameChange}
              placeholder="请输入章节名称"
              maxLength={200}
              disabled={isSubmitting}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 bg-white
                ${errors.name ? 'border-red-300' : 'border-gray-200'}
                disabled:bg-gray-100 disabled:cursor-not-allowed`}
            />
            <div className="mt-1 text-xs text-gray-700">
              {formData.name.length} / 200
            </div>
          </FormField>

          {/* 所属卷（占位符，暂禁用） */}
          <FormField label="所属卷" required={false}>
            <input
              type="text"
              value="暂不支持"
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed"
            />
            <div className="mt-1 text-xs text-gray-700">
              所属卷功能将在后续版本支持
            </div>
          </FormField>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-md font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md font-medium shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  {LOADING_SPINNER}
                  创建中...
                </>
              ) : (
                '创建章节'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChapterDialog;